'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTrack } from '@owlcat/app-shell';
import { VoiceToPromptButton } from '@/contexts/ai/components/VoiceToPromptButton';
import { extractCharacterFromDictation } from '@/contexts/characters/actions/extractCharacterFromDictation';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Character, CharacterGender } from '../types';
import { characterKeys } from '../queryKeys';
import { useCharacter } from '@/contexts/characters/hooks';
import { createCharacter } from '../actions/createCharacter';
import { getCharacterAppearances } from '../actions/getCharacterAppearances';
import { setCharacterAppearance } from '../actions/setCharacterAppearance';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { updateCharacter } from '../actions/updateCharacter';
import { generatePortrait } from '../actions/generatePortrait';
import { PHOTO_CONSENT_VERSION } from '../photoConsent';
import { analyzeCharacterPhoto } from '../actions/analyzeCharacterPhoto';
import { fileToBase64 } from '../utils/fileToBase64';
import { CharacterPortraitDisplay } from './CharacterPortraitDisplay';
import { CharacterPortraitControls, type GenerationSource } from './CharacterPortraitControls';
import { useTokenBalance, useImageCost } from '@/contexts/subscriptions';

const PHOTO_ANALYSIS_COST = 5;

import { SheetFooter } from '@/shared/components/ui/sheet';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Loader2 } from 'lucide-react';

export interface CharacterFormContentProps {
  character?: Character | null;
  onSuccess: (character: Character) => void;
  onCancel: () => void;
  /** Whether this is a fresh open (triggers form reset) */
  isActive: boolean;
  /** Reports busy state so parent can block Sheet close */
  onBusyChange?: (busy: boolean) => void;
}

interface FormData {
  name: string;
  gender?: CharacterGender;
  physicalDescription: string;
  personalityTraits: string;
  synonyms: string[];
  age: string;
}

const INITIAL_FORM_DATA: FormData = {
  name: '',
  gender: undefined,
  physicalDescription: '',
  personalityTraits: '',
  synonyms: [],
  age: '',
};

// Parse a comma-separated string into a clean string[] (trim, drop empties).
function parseSynonyms(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Normalize a Character row into FormData. Legacy rows may store 'neutral' as
// gender from an older default — treat that as "unspecified" so the Select
// renders the placeholder instead of an unknown value.
function characterToFormData(character: Character | null | undefined): FormData {
  if (!character) return INITIAL_FORM_DATA;
  return {
    name: character.name ?? '',
    gender:
      character.gender && character.gender !== 'neutral'
        ? (character.gender as CharacterGender)
        : undefined,
    physicalDescription: character.physicalDescription || '',
    personalityTraits: character.personalityTraits || '',
    synonyms: character.synonyms ?? [],
    age: character.age || '',
  };
}

export function CharacterFormContent({
  character,
  onSuccess,
  onCancel,
  isActive,
  onBusyChange,
}: CharacterFormContentProps) {
  const t = useTranslations('stories.characters.form');
  const track = useTrack();
  const tChar = useTranslations('stories.characters');
  const tImages = useTranslations('stories.characters.images');
  const tApp = useTranslations('stories.characters.appearances');
  const locale = useLocale();
  const queryClient = useQueryClient();
  // Lazy-initialize formData from the prop so the very first render already
  // reflects the character being edited. Relying on an activation effect to
  // seed formData after mount is fragile — it has to race with Radix Sheet
  // unmount/remount + controlled Select re-renders, and has produced cases
  // where gender stayed empty after a create→edit transition.
  const [formData, setFormData] = useState<FormData>(() => characterToFormData(character));
  // Raw text backing the synonyms input, so the user can type commas/spaces
  // freely; formData.synonyms holds the parsed string[] (source of truth).
  const [synonymsText, setSynonymsText] = useState<string>(
    () => (character?.synonyms ?? []).join(', ')
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local character state that can be updated without closing the sheet
  const [localCharacter, setLocalCharacter] = useState<Character | null | undefined>(character);

  // Determine edit mode from local character (supports create -> edit transition)
  const isEditMode = !!localCharacter?.id;

  // Appearances across stories — per-book generated looks reusable as primary.
  const { data: appearances = [] } = useQuery({
    queryKey: ['character-appearances', localCharacter?.id],
    queryFn: () => getCharacterAppearances(localCharacter!.id),
    enabled: isEditMode,
  });

  const handleSetAppearance = async (imageUrl: string) => {
    if (!localCharacter?.id) return;
    const result = await setCharacterAppearance(localCharacter.id, imageUrl);
    if (!result.success) return;
    setLocalCharacter((prev) => (prev ? { ...prev, primaryPortraitUrl: imageUrl } : prev));
    queryClient.invalidateQueries({ queryKey: characterKeys.detail(localCharacter.id) });
    queryClient.invalidateQueries({ queryKey: ['character-appearances', localCharacter.id] });
  };

  // Portrait generation controls state
  const [portraitSource, setPortraitSource] = useState<GenerationSource>('description');
  const [uploadedPhoto, setUploadedPhoto] = useState<File | null>(null);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  // Portrait generation state -- SSE replaces polling
  const [isPortraitGenerating, setIsPortraitGenerating] = useState(false);
  const [portraitGenerationFailed, setPortraitGenerationFailed] = useState(false);
  const [isGeneratingRequest, setIsGeneratingRequest] = useState(false);
  // Track previous isActive state to detect fresh opens
  const prevActiveRef = useRef(false);
  // Track whether the user has edited the form since activation. While
  // pristine, later-arriving fresh data from useCharacter() may refresh
  // descriptor fields (gender/age/etc) — useful when the parent passed
  // a stale character from a list that hadn't refetched yet.
  const formDirtyRef = useRef(false);

  // Photo analysis state
  const [isAnalyzingPhoto, setIsAnalyzingPhoto] = useState(false);

  // Premium ops are seamless (server enforces the allowance); the balance is
  // only refreshed after a spend so the header updates.
  const { refresh: refreshBalance } = useTokenBalance();

  // Get image cost from pricing config
  const { cost: portraitCost } = useImageCost();

  // SSE-driven auto-refetch: useCharacter hook auto-invalidates when
  // character.portrait.completed event arrives via EventRouter
  const { data: liveCharacter } = useCharacter(isEditMode ? localCharacter?.id : undefined);

  // Sync live character data (from SSE) into local state when portrait status changes
  useEffect(() => {
    if (!liveCharacter || !localCharacter?.id) return;
    if (liveCharacter.id !== localCharacter.id) return;

    // Check if portraits changed (new portrait arrived from async generation)
    const livePortraitCount = liveCharacter.portraits?.length ?? 0;
    const localPortraitCount = localCharacter.portraits?.length ?? 0;

    if (livePortraitCount > localPortraitCount || liveCharacter.portraitStatus !== localCharacter.portraitStatus) {
      // Portrait completed or status changed -- update local state
      if (isPortraitGenerating && livePortraitCount > localPortraitCount) {
        setIsPortraitGenerating(false);
        setPortraitGenerationFailed(false);
        setCustomPrompt('');
        setUploadedPhoto(null);
      }

      // Update failed status
      if (liveCharacter.portraitStatus === 'failed' && isPortraitGenerating) {
        setIsPortraitGenerating(false);
        setPortraitGenerationFailed(true);
      }

      setLocalCharacter((prev) =>
        prev
          ? {
              ...prev,
              portraits: liveCharacter.portraits,
              primaryPortraitUrl: liveCharacter.primaryPortraitUrl,
              portraitStatus: liveCharacter.portraitStatus,
            }
          : prev
      );
    }
  }, [liveCharacter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form ONLY when it activates fresh (false->true).
  // While active, localCharacter is the source of truth -- ignore character prop changes.
  useEffect(() => {
    const isActivating = isActive && !prevActiveRef.current;
    prevActiveRef.current = isActive;

    if (!isActivating) return;

    if (character) {
      setFormData(characterToFormData(character));
      setSynonymsText((character.synonyms ?? []).join(', '));
      setLocalCharacter(character);
      // Force a detail refetch so stale list data can't persist into the form
      // (e.g. user saved gender, closed sheet, reopened before list refetched).
      queryClient.invalidateQueries({ queryKey: characterKeys.detail(character.id) });
    } else {
      setFormData(INITIAL_FORM_DATA);
      setSynonymsText('');
      setLocalCharacter(null);
    }
    formDirtyRef.current = false;
    setPortraitSource('description');
    setUploadedPhoto(null);
    setCustomPrompt('');
    setIsPortraitGenerating(false);
    setPortraitGenerationFailed(false);
    setError(null);
  }, [isActive, character, queryClient]);

  // When the live (server) character arrives fresher than the prop (which may
  // have been stale list data), sync descriptor fields into the form — but
  // only while the form is pristine, so we never clobber user edits.
  useEffect(() => {
    if (!isActive || !liveCharacter || formDirtyRef.current) return;
    if (!localCharacter?.id || liveCharacter.id !== localCharacter.id) return;

    setFormData(characterToFormData(liveCharacter));
    setSynonymsText((liveCharacter.synonyms ?? []).join(', '));
  }, [isActive, liveCharacter, localCharacter?.id]);

  // Busy guard: true while any async operation that should block Sheet close
  const isBusy = isPortraitGenerating || isGeneratingRequest || isSubmitting;

  // Report busy state to parent
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // Handle cancel - check busy state before calling onCancel
  const handleCancel = () => {
    if (isBusy) return;

    if (localCharacter && isEditMode) {
      // Closing - sync the parent first if portraits were updated (onSuccess
      // alone keeps the sheet open, so ALWAYS fall through to onCancel — one
      // click closes).
      const hasPortraitChanges =
        localCharacter.portraits?.length !== character?.portraits?.length ||
        localCharacter.primaryPortraitUrl !== character?.primaryPortraitUrl;
      if (hasPortraitChanges) onSuccess(localCharacter);
    }
    onCancel();
  };

  // Build description from current form data (for portrait generation)
  const getCharacterDescription = () => {
    const parts: string[] = [];
    if (formData.physicalDescription) parts.push(formData.physicalDescription);
    if (formData.gender) parts.push(`Gender: ${formData.gender}`);
    return parts.join('. ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (isEditMode) {
        const result = await updateCharacter(localCharacter!.id, {
          name: formData.name,
          gender: formData.gender,
          physicalDescription: formData.physicalDescription,
          personalityTraits: formData.personalityTraits,
          synonyms: formData.synonyms,
        });

        if (result.success) {
          setLocalCharacter(result.data);
          // Invalidate React Query cache so list and detail reflect the update
          queryClient.invalidateQueries({ queryKey: characterKeys.detail(localCharacter!.id) });
          queryClient.invalidateQueries({ queryKey: characterKeys.lists() });
          onSuccess(result.data);
        } else {
          throw new Error(result.error);
        }
      } else {
        const result = await createCharacter({
          name: formData.name,
          gender: formData.gender,
          physicalDescription: formData.physicalDescription,
          personalityTraits: formData.personalityTraits,
          synonyms: formData.synonyms,
        });

        if (result.success) {
          track('character_created', { character_id: result.data.id, source: portraitSource === 'photo' && uploadedPhoto ? 'photo' : 'custom' });
          // Stay open -- transition to edit mode
          setLocalCharacter(result.data);
          onSuccess(result.data);
        } else {
          throw new Error(result.error);
        }
      }
    } catch (err: any) {
      console.error('Character form error:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // "Create & Generate" / "Generate Portrait" — seamless (no cost dialog).
  const handleCreateAndGenerate = () => {
    void performCreateAndGenerate();
  };

  const handleGenerateClick = () => {
    if (localCharacter) void performGenerate(localCharacter.id);
  };

  // Scroll the form body to the top so the portrait/spinner is visible
  // when generation kicks off (user may have submitted from bottom of form).
  const scrollFormToTop = () => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('character-form-element');
    el?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Create character then immediately generate portrait -- single linear flow
  const performCreateAndGenerate = async () => {
    setError(null);
    setIsSubmitting(true);
    // Show generating state immediately so the user sees the spinner from the start
    setIsPortraitGenerating(true);
    setPortraitGenerationFailed(false);
    scrollFormToTop();

    try {
      // Step 1: Create character
      const createResult = await createCharacter({
        name: formData.name,
        gender: formData.gender,
        physicalDescription: formData.physicalDescription,
        personalityTraits: formData.personalityTraits,
        synonyms: formData.synonyms,
      });

      if (!createResult.success) {
        throw new Error(createResult.error);
      }

      const newChar = createResult.data;
      setLocalCharacter(newChar);
      setIsSubmitting(false);

      // Step 2: Build portrait generation input (avatar is always the
      // neutral master-reference style — no style choice)
      const genInput: {
        characterId: string;
        referenceImageBase64?: string;
        customDescription?: string;
        photoConsentVersion?: string;
      } = {
        characterId: newChar.id,
      };

      if (portraitSource === 'photo' && uploadedPhoto) {
        genInput.referenceImageBase64 = await fileToBase64(uploadedPhoto);
        genInput.photoConsentVersion = PHOTO_CONSENT_VERSION;
      }

      if (customPrompt && customPrompt !== getCharacterDescription()) {
        genInput.customDescription = customPrompt;
      }

      // Step 3: Send portrait generation request
      setIsGeneratingRequest(true);
      const genResult = await generatePortrait(genInput);
      setIsGeneratingRequest(false);

      // Step 4: Notify parent AFTER generation request is sent
      track('character_created', { character_id: newChar.id, source: portraitSource === 'photo' && uploadedPhoto ? 'photo' : 'custom' });
      onSuccess(newChar);
      refreshBalance();

      if (!genResult.success) {
        throw new Error(genResult.error);
      }

      // Async generation -- SSE will pick up completion via useCharacter hook
      // isPortraitGenerating stays true until SSE delivers new portrait
    } catch (err: any) {
      console.error('Character create+generate error:', err);
      setIsPortraitGenerating(false);
      setPortraitGenerationFailed(true);
      setIsSubmitting(false);
      setIsGeneratingRequest(false);
      const msg = err.message || '';
      if (msg.includes('Insufficient tokens')) {
        setError(tImages('errors.insufficientTokens'));
      } else {
        setError(msg || tImages('errors.generationFailed'));
      }
    }
  };

  // Execute portrait generation for a character
  const performGenerate = async (characterId: string) => {
    setError(null);

    try {
      const input: {
        characterId: string;
        referenceImageBase64?: string;
        customDescription?: string;
        photoConsentVersion?: string;
      } = {
        characterId,
      };

      if (portraitSource === 'photo' && uploadedPhoto) {
        input.referenceImageBase64 = await fileToBase64(uploadedPhoto);
        input.photoConsentVersion = PHOTO_CONSENT_VERSION;
      }

      if (customPrompt && customPrompt !== getCharacterDescription()) {
        input.customDescription = customPrompt;
      }

      // Show generating state immediately (before server responds)
      setIsPortraitGenerating(true);
      setPortraitGenerationFailed(false);
      scrollFormToTop();

      setIsGeneratingRequest(true);
      const result = await generatePortrait(input);
      setIsGeneratingRequest(false);

      if (!result.success) {
        throw new Error(result.error);
      }

      refreshBalance();

      // Async generation -- SSE handles portrait completion via useCharacter hook
      // isPortraitGenerating stays true until SSE delivers new portrait
    } catch (err: any) {
      console.error('Failed to generate image:', err);
      setIsPortraitGenerating(false);
      setPortraitGenerationFailed(true);
      setIsGeneratingRequest(false);
      const msg = err.message || '';
      if (msg.includes('Insufficient tokens')) {
        setError(tImages('errors.insufficientTokens'));
      } else {
        setError(msg || tImages('errors.generationFailed'));
      }
    }
  };

  // Analyze photo to extract description
  const handleAnalyzePhotoClick = () => {
    void handleAnalyzePhotoConfirm();
  };

  const handleAnalyzePhotoConfirm = async () => {
    if (!uploadedPhoto) return;
    setError(null);
    setIsAnalyzingPhoto(true);

    try {
      const imageBase64 = await fileToBase64(uploadedPhoto);
      const result = await analyzeCharacterPhoto(imageBase64, locale);

      if (result.success && result.data.physicalDescription) {
        setFormData((prev) => {
          const updates: Partial<FormData> = {
            physicalDescription: result.data.physicalDescription,
          };
          // Fill empty fields from analysis (name is intentionally never auto-filled)
          if (!prev.age && result.data.age) updates.age = result.data.age;
          if (!prev.gender && result.data.gender) {
            const genderMap: Record<string, CharacterGender> = {
              'boy': CharacterGender.Male, 'girl': CharacterGender.Female,
              'man': CharacterGender.Male, 'woman': CharacterGender.Female,
              'мальчик': CharacterGender.Male, 'девочка': CharacterGender.Female,
              'мужчина': CharacterGender.Male, 'женщина': CharacterGender.Female,
              'хлопчик': CharacterGender.Male, 'дівчинка': CharacterGender.Female,
              'chlapec': CharacterGender.Male, 'dívka': CharacterGender.Female,
            };
            const mapped = genderMap[result.data.gender.toLowerCase()];
            if (mapped) updates.gender = mapped;
          }
          if (!prev.personalityTraits && result.data.personalityTraits) {
            updates.personalityTraits = result.data.personalityTraits;
          }
          return { ...prev, ...updates };
        });
        refreshBalance();
      } else if (!result.success) {
        throw new Error(result.error);
      }
    } catch (err: any) {
      console.error('Photo analysis error:', err);
      setError(err.message || t('analyzePhotoError'));
    } finally {
      setIsAnalyzingPhoto(false);
    }
  };

  const handleChange = (
    field: keyof FormData,
    value: string | CharacterGender | undefined
  ) => {
    formDirtyRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Dictate the character: record → STT → LLM extract → prefill the form fields
  // (only the ones the LLM confidently returned; the rest stay as-is).
  const [dictating, setDictating] = useState(false);
  const handleDictated = useCallback(
    async (text: string) => {
      setDictating(true);
      const res = await extractCharacterFromDictation(text, locale).catch(() => null);
      setDictating(false);
      if (res?.success && res.data) {
        const f = res.data;
        formDirtyRef.current = true;
        const gender =
          f.gender === 'male' || f.gender === 'female' || f.gender === 'neutral'
            ? (f.gender as CharacterGender)
            : undefined;
        setFormData((prev) => ({
          ...prev,
          name: f.name ?? prev.name,
          physicalDescription: f.physicalDescription ?? prev.physicalDescription,
          personalityTraits: f.personalityTraits ?? prev.personalityTraits,
          gender: gender ?? prev.gender,
          age: f.age ?? prev.age,
        }));
      }
    },
    [locale],
  );

  const canSubmit = () => {
    if (!formData.name.trim()) return false;

    if (isEditMode) {
      return true;
    }

    // Manual creation requires at least one description field
    return (
      formData.physicalDescription.trim().length > 0 ||
      formData.personalityTraits.trim().length > 0
    );
  };

  const hasPhoto = portraitSource === 'photo' && uploadedPhoto !== null;
  const canCreateAndGenerate = formData.name.trim().length > 0 &&
    (canSubmit() || hasPhoto);

  return (
    <>
      <form id="character-form-element" data-testid="character-form" onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto px-4 space-y-4 pb-4">
        {/* Portrait Display -- hero area at top */}
        <CharacterPortraitDisplay
          primaryPortraitUrl={localCharacter?.primaryPortraitUrl ?? undefined}
          faceUrl={localCharacter?.faceReferenceUrl ?? undefined}
          characterName={localCharacter?.name || formData.name}
          isGenerating={isPortraitGenerating}
          generationFailed={portraitGenerationFailed}
        />

        {/* Dictate the character — record → speech-to-text → LLM extract → prefill. */}
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border p-3">
          <p className="min-w-0 text-xs text-muted-foreground">
            {dictating ? t('dictateExtracting') : t('dictateHint')}
          </p>
          <VoiceToPromptButton onText={handleDictated} disabled={dictating} />
        </div>

        {/* Character Info Fields */}
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">{t('name')}</Label>
            <Input
              data-testid="character-name-input"
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('namePlaceholder')}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gender">{t('gender')}</Label>
            <Select
              value={formData.gender || 'unspecified'}
              onValueChange={(value) => handleChange('gender', value === 'unspecified' ? undefined : value as CharacterGender)}
              disabled={isSubmitting}
            >
              <SelectTrigger data-testid="character-gender-select" id="gender">
                <SelectValue placeholder={t('genderPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unspecified">{t('genderOptions.notSpecified')}</SelectItem>
                <SelectItem value="male">{t('genderOptions.male')}</SelectItem>
                <SelectItem value="female">{t('genderOptions.female')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Age input removed — a story character's "age" said little; the AGE
              that matters is the audience's ("для кого"), set on the story. When a
              photo is used, the analyzer still fills age for the portrait prompt. */}
        </div>

        {/* Character description fields — each labelled with what it drives */}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="physicalDescription">{t('physicalDescription')}</Label>
            <p className="text-xs text-muted-foreground">{t('physicalDescriptionHint')}</p>
            <Textarea
              data-testid="character-physical-description-input"
              id="physicalDescription"
              value={formData.physicalDescription}
              onChange={(e) => handleChange('physicalDescription', e.target.value)}
              placeholder={t('physicalDescriptionPlaceholder')}
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="personalityTraits">{t('personalityTraits')}</Label>
            <p className="text-xs text-muted-foreground">{t('personalityTraitsHint')}</p>
            <Textarea
              data-testid="character-personality-input"
              id="personalityTraits"
              value={formData.personalityTraits}
              onChange={(e) => handleChange('personalityTraits', e.target.value)}
              placeholder={t('personalityTraitsPlaceholder')}
              rows={2}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="synonyms">{tChar('synonyms')}</Label>
            <p className="text-xs text-muted-foreground">{tChar('synonymsHint')}</p>
            <Input
              data-testid="character-synonyms-input"
              id="synonyms"
              value={synonymsText}
              onChange={(e) => {
                formDirtyRef.current = true;
                setSynonymsText(e.target.value);
                setFormData((prev) => ({ ...prev, synonyms: parseSynonyms(e.target.value) }));
              }}
              placeholder={tChar('synonymsPlaceholder')}
              disabled={isSubmitting}
            />
          </div>

        </div>

        {/* Portrait Generation Controls */}
        <CharacterPortraitControls
          portraitSource={portraitSource}
          onPortraitSourceChange={setPortraitSource}
          uploadedPhoto={uploadedPhoto}
          onUploadedPhotoChange={setUploadedPhoto}
          photoConsent={photoConsent}
          onPhotoConsentChange={setPhotoConsent}
          customPrompt={customPrompt}
          onCustomPromptChange={setCustomPrompt}
          characterDescription={getCharacterDescription()}
          characterId={isEditMode ? localCharacter!.id : null}
          onGenerateClick={handleGenerateClick}
          isGenerating={isGeneratingRequest || isPortraitGenerating}
          portraitCost={portraitCost}
          onAnalyzePhoto={handleAnalyzePhotoClick}
          isAnalyzingPhoto={isAnalyzingPhoto}
          photoAnalysisCost={PHOTO_ANALYSIS_COST}
          disabled={isSubmitting}
        />

        {/* Appearance across stories -- reuse a per-book look as the primary portrait */}
        {isEditMode && appearances.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium">{tApp('title')}</h3>
            <p className="text-xs text-muted-foreground">{tApp('reuseWarning')}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {appearances.map((appearance) => {
                const isCurrent = appearance.imageUrl === localCharacter?.primaryPortraitUrl;
                return (
                  <div
                    key={appearance.imageUrl}
                    className={`overflow-hidden rounded-lg border ${
                      isCurrent ? 'ring-2 ring-primary' : ''
                    }`}
                  >
                    <div className="relative aspect-[3/4] w-full bg-muted">
                      <Image
                        src={mediaSrc(appearance.imageUrl)}
                        alt={appearance.bookTitle}
                        fill
                        unoptimized
                        className="object-cover"
                        sizes="(max-width: 640px) 50vw, 33vw"
                      />
                      {isCurrent && (
                        <span className="absolute left-1.5 top-1.5 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                          {tApp('current')}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1.5 p-2">
                      <p className="truncate text-xs font-medium">{appearance.bookTitle}</p>
                      <p className="truncate text-[11px] capitalize text-muted-foreground">
                        {appearance.style}
                      </p>
                      <Button
                        type="button"
                        variant={isCurrent ? 'secondary' : 'outline'}
                        size="sm"
                        className="w-full"
                        disabled={isCurrent}
                        onClick={() => handleSetAppearance(appearance.imageUrl)}
                      >
                        {tApp('setPrimary')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </form>

      {/* Footer lives OUTSIDE the form so it stays pinned while the form body
          scrolls. Submit buttons use `form="character-form-element"` to stay
          wired to the form. Sheet is a flex column (`h-full flex flex-col`)
          so the scrolling form fills remaining height and the footer's
          `mt-auto` keeps it at the bottom on both desktop and mobile. */}
      <SheetFooter>
        <Button
          data-testid="character-form-cancel-button"
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </Button>
        {isEditMode ? (
          <Button
            data-testid="character-form-submit-button"
            type="submit"
            form="character-form-element"
            disabled={!canSubmit() || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('update')}
          </Button>
        ) : canCreateAndGenerate ? (
          <Button
            data-testid="character-form-create-generate-button"
            type="button"
            onClick={handleCreateAndGenerate}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('createAndGenerate')}
          </Button>
        ) : (
          <Button
            data-testid="character-form-submit-button"
            type="submit"
            form="character-form-element"
            disabled={!canSubmit() || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('create')}
          </Button>
        )}
      </SheetFooter>

    </>
  );
}
